import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
  Fade,
  Skeleton,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTeacher } from '../../context/TeacherContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoRenewIcon from '@mui/icons-material/Autorenew';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';
import SchoolIcon from '@mui/icons-material/School';

interface SubscriptionInfo {
  id?: string;
  status: string;
  plan: string;
  amount: string;
  interval: string;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  trialEnd?: number;
  daysRemaining?: number;
  isTrial: boolean;
}

const SUBSCRIPTION_BACKEND_URL = process.env.REACT_APP_SUBSCRIPTION_URL || 'http://localhost:5002';

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const { teacher, setTeacher } = useTeacher();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [isYearly, setIsYearly] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [actionLoading, setActionLoading] = useState<'cancel' | 'reactivate' | null>(null);
  const [trialExpired, setTrialExpired] = useState(false);

  const fetchSubscriptionStatus = async () => {
    if (!teacher?._id) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(
        `${SUBSCRIPTION_BACKEND_URL}/subscription/status?teacherId=${encodeURIComponent(teacher._id)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }

      const data = await response.json();
      
      if (data.hasSubscription && data.subscription) {
        setSubscriptionInfo(data.subscription);
        setTrialExpired(false);
        setError(null);
      } else if (data.status === 'trial_expired') {
        setSubscriptionInfo(null);
        setTrialExpired(true);
        setError(null);
      } else {
        // No subscription and no trial - auto-start trial
        const trialStarted = await autoStartTrial();
        if (!trialStarted) {
          // If auto-start failed, show trial expired view as fallback
          setTrialExpired(true);
          setError('Unable to start trial automatically. Please subscribe to continue.');
        }
      }
    } catch (err: any) {
      console.error('Error fetching subscription:', err);
      if (err.name === 'AbortError') {
        setError('Connection timed out. Please make sure the subscription service is running.');
      } else {
        setError('Unable to connect to subscription service. Please try again later.');
      }
      // Show trial expired view as fallback so user can still subscribe
      setTrialExpired(true);
    } finally {
      setLoading(false);
    }
  };

  const autoStartTrial = async (): Promise<boolean> => {
    if (!teacher?._id) return false;

    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${SUBSCRIPTION_BACKEND_URL}/start-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          teacherId: teacher._id,
          email: teacher.email
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.success) {
        // Update teacher context with trial info
        if (teacher) {
          setTeacher({
            ...teacher,
            subscription: {
              status: 'trial' as const,
              plan: 'premium' as const,
              trialEnd: data.trialEnd,
              subscriptionId: undefined
            }
          });
        }
        // Refresh status to show trial
        await fetchSubscriptionStatus();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error auto-starting trial:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [teacher?._id]);

  const handleSubscribe = async () => {
    if (!teacher?._id) {
      setError('Please log in to subscribe');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const response = await fetch(`${SUBSCRIPTION_BACKEND_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacherId: teacher._id,
          email: teacher.email,
          name: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
          interval: isYearly ? 'yearly' : 'monthly'
        }),
      });

      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to start checkout. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!teacher?._id) return;

    try {
      setActionLoading('cancel');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${SUBSCRIPTION_BACKEND_URL}/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ teacherId: teacher._id }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchSubscriptionStatus();
      } else {
        setError(data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      setError('Failed to cancel subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async () => {
    if (!teacher?._id) return;

    try {
      setActionLoading('reactivate');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${SUBSCRIPTION_BACKEND_URL}/subscription/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ teacherId: teacher._id }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchSubscriptionStatus();
      } else {
        setError(data.error || 'Failed to reactivate subscription');
      }
    } catch (err) {
      setError('Failed to reactivate subscription');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 4, mt: 4 }}>
        <Skeleton variant="text" height={60} width={400} sx={{ mx: 'auto' }} />
        <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 3, mt: 3 }} />
      </Box>
    );
  }

  // TRIAL EXPIRED - Show payment required screen
  if (trialExpired) {
    return (
      <Fade in>
        <Box sx={{ maxWidth: 600, mx: 'auto', p: { xs: 2, md: 4 }, mt: 2 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Your free trial has ended. Subscribe to continue using all features.
            </Alert>
          )}

          <Paper elevation={4} sx={{ borderRadius: 4, border: 2, borderColor: 'primary.main' }}>
            <Box sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: 3,
              textAlign: 'center'
            }}>
              <LockIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
              <Typography variant="h5" fontWeight="bold">
                Subscribe to Continue
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {/* Monthly/Yearly Toggle */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
                <Button
                  variant={!isYearly ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setIsYearly(false)}
                  sx={{ borderRadius: 2 }}
                >
                  Monthly
                </Button>
                <Button
                  variant={isYearly ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setIsYearly(true)}
                  sx={{ borderRadius: 2 }}
                >
                  Yearly
                </Button>
                <Chip 
                  label="Save 17%" 
                  size="small" 
                  color="success" 
                  sx={{ ml: 1 }}
                />
              </Box>

              {/* Price */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h2" fontWeight="bold" color="primary">
                  {isYearly ? '$59.99' : '$5.99'}
                  <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                    /{isYearly ? 'year' : 'month'}
                  </Typography>
                </Typography>
                {isYearly && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    Only $5.00/month, billed annually
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Features */}
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Premium includes:
              </Typography>
              <Box sx={{ mb: 3 }}>
                {[
                  'Unlimited AI Lesson Plans',
                  'Unlimited AI Teaching Chat',
                  'Behavior Tracking & Reports',
                  'Passage Generator',
                  'Math Word Problems Generator',
                  'Targeted Learning Plans',
                  'Export to PDF & Word',
                  'Priority Email Support'
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* CTA Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubscribe}
                disabled={processing}
                sx={{ 
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2
                }}
              >
                {processing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Subscribe ${isYearly ? 'Yearly' : 'Monthly'} - ${isYearly ? '$59.99' : '$5.99'}`
                )}
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center', display: 'block' }}>
                Secure payment via Stripe. Cancel anytime.
              </Typography>
            </CardContent>
          </Paper>
        </Box>
      </Fade>
    );
  }

  // ACTIVE TRIAL - Show trial status with option to subscribe early
  if (subscriptionInfo?.status === 'trial') {
    const renewalDate = subscriptionInfo.trialEnd 
      ? new Date(subscriptionInfo.trialEnd * 1000).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      : 'Unknown';

    return (
      <Fade in>
        <Box sx={{ maxWidth: 700, mx: 'auto', p: 4, mt: 4 }}>
          <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ 
              bgcolor: 'success.main', 
              color: 'primary.contrastText',
              p: 4,
              textAlign: 'center'
            }}>
              <StarIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Free Trial Active
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {subscriptionInfo.daysRemaining} days remaining
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Status */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Chip 
                  label="Free Trial" 
                  color="success" 
                  size="medium"
                  sx={{ fontSize: '1rem', px: 2, py: 2.5, borderRadius: 2 }}
                />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  You're enjoying full access to all premium features.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  Trial ends: <strong>{renewalDate}</strong>
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Features */}
              <Typography variant="h6" gutterBottom fontWeight="medium">
                Your Premium Benefits
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  'Unlimited AI Lesson Plans',
                  'Unlimited AI Chat',
                  'Behavior Tracking',
                  'Passage Generator',
                  'Math Word Problems',
                  'Targeted Learning',
                  'Priority Support'
                ].map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Early Subscribe Option */}
              <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Want to continue after your trial?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Subscribe now to keep unlimited access. Cancel anytime.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsYearly(false)}
                    sx={{ flex: 1 }}
                  >
                    Monthly $5.99
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setIsYearly(true)}
                    sx={{ flex: 1 }}
                  >
                    Yearly $59.99
                  </Button>
                </Box>
                
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubscribe}
                  disabled={processing}
                  sx={{ mt: 2 }}
                >
                  {processing ? <CircularProgress size={20} /> : `Subscribe ${isYearly ? 'Yearly' : 'Monthly'}`}
                </Button>
              </Box>
            </CardContent>
          </Paper>
        </Box>
      </Fade>
    );
  }

  // ACTIVE PAID SUBSCRIPTION
  if (subscriptionInfo?.status === 'active' || subscriptionInfo?.status === 'trialing') {
    const renewalDate = subscriptionInfo.currentPeriodEnd 
      ? new Date(subscriptionInfo.currentPeriodEnd * 1000).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      : 'Unknown';

    return (
      <Fade in>
        <Box sx={{ maxWidth: 700, mx: 'auto', p: 4, mt: 4 }}>
          <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ 
              bgcolor: 'primary.main', 
              color: 'primary.contrastText',
              p: 4,
              textAlign: 'center'
            }}>
              <SchoolIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Premium Active
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                You're all set with LevelUpTeaching Premium
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Plan Details */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Chip 
                  label="Premium" 
                  color="primary" 
                  size="medium"
                  sx={{ fontSize: '1rem', px: 2, py: 2.5, borderRadius: 2 }}
                />
                <Typography variant="h3" sx={{ mt: 3, fontWeight: 'bold', color: 'primary.main' }}>
                  {subscriptionInfo.amount}
                  <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                    /{subscriptionInfo.interval === 'yearly' ? 'year' : 'month'}
                  </Typography>
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Status */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CheckCircleIcon color="success" />
                  <Typography>
                    Status: <strong>{subscriptionInfo.cancelAtPeriodEnd ? 'Cancelling at period end' : 'Active'}</strong>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CalendarTodayIcon color="action" />
                  <Typography>
                    {subscriptionInfo.cancelAtPeriodEnd ? 'Access until:' : 'Next billing date:'}{' '}
                    <strong>{renewalDate}</strong>
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                {subscriptionInfo.cancelAtPeriodEnd ? (
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleReactivate}
                    disabled={!!actionLoading}
                    startIcon={actionLoading === 'reactivate' ? <CircularProgress size={20} /> : <AutoRenewIcon />}
                  >
                    {actionLoading === 'reactivate' ? 'Reactivating...' : 'Reactivate Subscription'}
                  </Button>
                ) : (
                  <>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={handleCancel}
                      disabled={!!actionLoading}
                      startIcon={actionLoading === 'cancel' ? <CircularProgress size={20} color="error" /> : <CancelIcon />}
                    >
                      {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Subscription'}
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => navigate('/dashboard')}
                    >
                      Go to Dashboard
                    </Button>
                  </>
                )}
              </Box>

              {subscriptionInfo.cancelAtPeriodEnd && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  Your subscription will remain active until {renewalDate}. You can reactivate anytime before then.
                </Alert>
              )}
            </CardContent>
          </Paper>
        </Box>
      </Fade>
    );
  }

  // LOADING STATE (should rarely show)
  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 4, mt: 4, textAlign: 'center' }}>
      <CircularProgress size={48} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        Loading your subscription...
      </Typography>
    </Box>
  );
};

export default Subscription;
